import * as R from 'ramda';
import { CSS } from '@dnd-kit/utilities';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import React, { useMemo, useState, useCallback } from 'react';
import { pure, compose, withState, withHandlers } from 'react-recompose';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { useSortable, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
// components
import { setName } from '../table/components/helpers';
import { FormFooter, TextComponent } from '../../components';
// forms
import { Checkbox } from '../../forms/formik/fieldset2/ui';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// icons
import * as I from '../../svgs';
// ui
import { Box, Flex, scrollableContainerCss4px } from '../../ui';
//////////////////////////////////////////////////

const dndModifiers = [restrictToVerticalAxis, restrictToParentElement];

const Field = ({ name, label, sticky, cursor, checked, disabled, handleChangeCheckbox }) => (
  <Flex
    my='5px'
    height={45}
    width='100%'
    p='10px 20px'
    cursor={cursor}
    borderRadius='4px'
    alignItems='center'
    justifyContent='space-between'
    backgroundColor={G.getTheme('colors.white')}
    boxShadow={`0 2px 5px 0 ${G.getTheme('colors.boxShadowGrey')}`}
  >
    <Flex>
      <Box width={20}>{sticky ? null : I.dnd(G.getTheme('colors.dark.blue'), 20, 20)}</Box>
      <TextComponent
        mx={10}
        title={label}
        fontSize={16}
        maxWidth={340}
        withEllipsis={true}
      >
        {label}
      </TextComponent>
    </Flex>
    {
      R.not(disabled) &&
      <Box cursor='pointer' onPointerDown={G.stopPropagation}>
        <Checkbox
          name={name}
          type='checkbox'
          checked={checked}
          onChange={(e) => handleChangeCheckbox(e, name)}
        />
      </Box>
    }
  </Flex>
);

const SortableField = (props) => {
  const { name, isHidden, ...fieldProps } = props;

  const {
    listeners,
    transform,
    attributes,
    setNodeRef,
    transition,
  } = useSortable({
    id: name,
    animateLayoutChanges: () => false,
  });

  const style = {
    transition,
    cursor: 'grab',
    opacity: isHidden ? 0.1 : 1,
    transform: CSS.Translate.toString(transform),
  };

  return (
    <div
      style={style}
      ref={setNodeRef}
      {...attributes}
      {...listeners}
    >
      <Field name={name} cursor='grab' {...fieldProps} />
    </div>
  );
};

const enhance = compose(
  withState('reportFieldList', 'setReportFieldList', ({ uiReportFields, defaultReportFields }) => {
    if (G.isNilOrEmpty(uiReportFields)) {
      return R.map((name) => ({ name, [GC.FIELD_CHECKED]: true }), defaultReportFields);
    }

    return R.compose(
      R.map((name) => ({ name, [GC.FIELD_CHECKED]: R.includes(name, uiReportFields) })),
      R.uniq,
      R.concat(uiReportFields),
    )(defaultReportFields);
  }),
  withHandlers({
    handleChangeCheckbox: (props) => ({ target }, fieldName) => {
      const { reportFieldList, setReportFieldList } = props;

      const fieldIndex = R.findIndex(R.propEq(fieldName, GC.FIELD_NAME), reportFieldList);

      const fieldsUpdated = R.assocPath(
        [fieldIndex, GC.FIELD_CHECKED],
        G.getPropFromObject(GC.FIELD_CHECKED, target),
        reportFieldList,
      );

      setReportFieldList(fieldsUpdated);
    },
    handleSaveFields: (props) => () => {
      const {
        closeModal,
        reportGuid,
        reportFieldList,
        setUiReportFields,
        disableFieldsStorage,
      } = props;

      const reportFields = R.compose(
        R.map(R.prop(GC.FIELD_NAME)),
        R.filter(R.prop(GC.FIELD_CHECKED)),
      )(reportFieldList);

      setUiReportFields(reportFields);

      if (R.not(G.isTrue(disableFieldsStorage))) {
        G.setUiReportFieldsToLocalStorage(reportGuid, reportFields);
      }

      closeModal();
    },
    onDragEnd: (props) => ({ over, active }) => {
      const { reportFieldList, setReportFieldList } = props;

      const overId = R.prop('id', over);
      const activeId = R.prop('id', active);

      if (R.or(R.not(over), R.equals(activeId, overId))) return;

      const endIndex = R.findIndex(R.propEq(overId, GC.FIELD_NAME), reportFieldList);
      const startIndex = R.findIndex(R.propEq(activeId, GC.FIELD_NAME), reportFieldList);

      if (R.equals(startIndex, endIndex)) return;

      setReportFieldList(G.reorderList(reportFieldList, startIndex, endIndex));
    },
  }),
  pure,
);

const getTitleName = (field, { report, columnSettings }) => {
  const name = setName(field, { columnSettings });

  if (G.isNotNilAndNotEmpty(R.trim(name))) return name;

  const fields = R.propOr([], 'fields', report);

  const reportField = R.find(R.propEq(R.prop(GC.FIELD_NAME, field), GC.FIELD_NAME), fields);

  if (G.isNilOrEmpty(reportField)) return name;

  const { customTitleComponent } = reportField;

  if (G.isFunction(customTitleComponent)) return customTitleComponent();

  return name;
};

const EditViewForm = enhance((props) => {
  const {
    report,
    onDragEnd,
    columnSettings,
    reportFieldList,
    handleSaveFields,
    disableFieldHandler,
    resetUiReportFields,
    handleChangeCheckbox,
  } = props;

  const actionButtons = [
    {
      action: resetUiReportFields,
      displayText: G.getWindowLocale('actions:reset', 'Reset'),
    },
  ];

  const [draggingFieldName, setDraggingFieldName] = useState(null);

  const stickyList = useMemo(() => R.filter((field) => {
    const { name } = field;

    return R.pathOr(false, [name, 'sticky'], columnSettings);
  }, reportFieldList), [reportFieldList]);

  const draggableList = useMemo(
    () => R.slice(R.length(stickyList), Infinity, reportFieldList),
    [stickyList, reportFieldList]);

  const sortableIds = useMemo(
    () => R.map(R.prop(GC.FIELD_NAME), draggableList),
    [draggableList],
  );

  const handleDragStart = useCallback(({ active }) => {
    setDraggingFieldName(R.prop('id', active));
  }, []);

  const handleDragEnd = useCallback((event) => {
    setDraggingFieldName(null);

    onDragEnd(event);
  }, [onDragEnd]);

  const handleDragCancel = useCallback(() => {
    setDraggingFieldName(null);
  }, []);

  const draggingField = R.find(R.propEq(draggingFieldName, GC.FIELD_NAME), draggableList);

  return (
    <Flex height='100%' flexDirection='column' justifyContent='space-between'>
      <DndContext
        modifiers={dndModifiers}
        onDragEnd={handleDragEnd}
        onDragStart={handleDragStart}
        onDragCancel={handleDragCancel}
      >
        <Box
          p={15}
          mb={30}
          width='100%'
          overflow='auto'
          css={scrollableContainerCss4px}
        >
          {
            G.isNotNilAndNotEmpty(stickyList) && (
              stickyList.map((field) => {
                const { name } = field;

                const label = getTitleName(field, { report, columnSettings });

                return (
                  <Field
                    key={name}
                    {...field}
                    label={label}
                    sticky={true}
                    disabled={true}
                  />
                );
              })
            )
          }
          <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
            {
              draggableList.map((field) => {
                const { name } = field;

                const isHidden = R.equals(name, draggingFieldName);

                const label = getTitleName(field, { report, columnSettings });

                const disabled = G.isFunction(disableFieldHandler) ? disableFieldHandler(name) : false;

                return (
                  <SortableField
                    key={name}
                    {...field}
                    label={label}
                    isHidden={isHidden}
                    disabled={disabled}
                    handleChangeCheckbox={handleChangeCheckbox}
                  />
                );
              })
            }
          </SortableContext>
        </Box>
        <DragOverlay>
          {
            draggingField ? (
              <Field
                {...draggingField}
                cursor='grabbing'
                handleChangeCheckbox={handleChangeCheckbox}
                label={getTitleName(draggingField, { report, columnSettings })}
                disabled={G.isFunction(disableFieldHandler) ? disableFieldHandler(draggingFieldName) : false}
              />
            ) : null
          }
        </DragOverlay>
      </DndContext>
      <FormFooter
        actionButtons={actionButtons}
        submitAction={handleSaveFields}
        submitBtnText={G.getWindowLocale('actions:save', 'Save')}
        boxStyles={{ p: 15, bottom: '0', position: 'sticky', bg: G.getTheme('colors.white') }}
      />
    </Flex>
  );
});

export { EditViewForm };
